import * as fs from 'fs';
import * as os from 'os';
import * as path from 'path';
import express from 'express';
import { ArgError } from '../errors';
import { lazyRequire } from '../commons/lazyRequire';
import type { AddressInfo } from 'net';
import type { getLogger } from '../commons/logger';
import type { TestRunStatusTestResult } from '../testRunStatus';
import type { AgentModeOptions, RunnerOptions } from '../runOptions';

const DEFAULT_PATH = path.join(os.tmpdir(), 'testim/rca/');

const stepFileMap: { [stepId: string]: string } = {};
const resultFileMap: { [stepId: string]: string[] } = {};

const TYPE_RENAMES = {
    'test-log': 'consoleLogs',
    'har-file': 'networkLogs',
} as const;
const TYPES = Object.keys(TYPE_RENAMES) as Array<keyof typeof TYPE_RENAMES>;

export function mapFilesToLocalDrive(test: TestRunStatusTestResult, logger: ReturnType<typeof getLogger>) {
    try {
        test.failurePath = (test.failurePath || []).map(f => Object.assign(f, stepFileMap[f.id] ? { screenshot: stepFileMap[f.id] } : {}));
        Object.keys(resultFileMap).forEach(type => {
            test.assets ||= {};
            test.assets[TYPE_RENAMES[type as keyof typeof TYPE_RENAMES]] = resultFileMap[type];
        });

        test.assets ||= {};
        test.assets.screenshots = Object.values(stepFileMap);
    } catch (err) {
        if (logger) {
            logger.error('failed to map files to local drive', { err });
        }
        // set default values.
        test.failurePath ||= [];
        test.assets ||= {};
        test.assets.screenshots ||= [];
    }
}

export async function initServer({ agentPort, agentBind, saveRCALocally }: Partial<Pick<AgentModeOptions, 'agentPort' | 'agentBind'> & Pick<RunnerOptions, 'saveRCALocally'>>) {
    const multer = await lazyRequire('multer') as typeof import('multer');
    const saveRCALocallyStr = typeof saveRCALocally === 'string' ? saveRCALocally : DEFAULT_PATH;

    await fs.promises.mkdir(saveRCALocallyStr, { recursive: true });

    const upload = multer({
        storage: multer.diskStorage({
            async destination(req, file, cb) {
                const metadata = JSON.parse(req.body.metadata || '{}');
                if (!metadata.testResultId) {
                    return cb(new Error('missing testResultId'), '');
                }
                const destination = path.join(saveRCALocallyStr, metadata.testResultId);
                try {
                    await fs.promises.mkdir(destination, { recursive: true });
                } catch (err) {
                    return cb(err, '');
                }
                return cb(null, destination);
            },
            filename(req, file, cb) {
                const { fileName } = req.body;
                const metadata = JSON.parse(req.body.metadata || '{}');
                if (!metadata.stepId && !fileName) {
                    return cb(new Error('missing stepId or fileName'), '');
                }
                if (metadata.stepId) {
                    const format = path.extname(fileName);
                    return cb(null, `step_${metadata.stepId}_${metadata.stepName || ''}${format}`.replace(/[/\\?%*:|"<>\s]/g, '-'));
                }
                return cb(null, fileName);
            },
        }),
    });

    const app = express();
    app.disable('x-powered-by');

    app.post('/', upload.single('file'), (req, res) => {
        const metadata = JSON.parse(req.body.metadata || '{}');
        if (metadata.stepId) {
            stepFileMap[metadata.stepId] = req.file.path;
        }
        if (metadata.testResultId && TYPES.includes(metadata.subType)) {
            resultFileMap[metadata.subType] ||= [];
            resultFileMap[metadata.subType].push(req.file.path);
        }
        res.sendStatus(200);
    });
    app.use((req, res) => res.status(404).send('Endpoint Not Found'));

    const { createServer } = await import('http');
    return await new Promise<AddressInfo>((resolve, reject) => {
        const server = createServer(app);
        server.listen(agentPort, agentBind);
        server.on('error', onError);
        server.on('listening', () => resolve(server.address() as AddressInfo));

        function onError(error: Error & { syscall?: string; code?: string }) {
            if (error.syscall !== 'listen') {
                return reject(error);
            }

            // handle specific listen errors with friendly messages
            switch (error.code) {
                case 'EACCES':
                case 'EPERM':
                    return reject(new ArgError(`Port ${agentPort} requires elevated privileges`));
                case 'EADDRINUSE':
                    return reject(new ArgError(`Port ${agentPort} is already in use`));
                default:
                    return reject(error);
            }
        }
    });
}
